import { getSharedApiService } from './apiService';
import { apiRoutes } from '@/config/routes';
import type {
  Organization,
  Logo,
  UploadLogoParams,
  UpdateOrganizationParams,
} from '@/types/organization';

// Get API service instance once at module level
const api = getSharedApiService();

/**
 * Get organization by ID
 * GET /api/v2/organizations/{organization_id}
 */
export const getOrganization = async (organizationId: string): Promise<Organization> => {
  const response = await api.get<Organization>(
    apiRoutes.organizations.getById(organizationId)
  );
  
  return response.data;
};

/**
 * Update organization by ID
 * PUT /api/v2/organizations/{organization_id}
 * Supports both name and logo upload in single request
 */
export const updateOrganization = async (
  organizationId: string,
  params: UpdateOrganizationParams
): Promise<Organization> => {
  const formData = new FormData();
  
  // Add fields to form data (only if provided)
  if (params.name !== undefined) {
    formData.append('name', params.name);
  }
  
  if (params.logo) {
    formData.append('logo', params.logo);
  }
  
  if (params.sso_enabled !== undefined) {
    formData.append('sso_enabled', String(params.sso_enabled));
  }
  
  if (params.sso_config) {
    formData.append('sso_config', JSON.stringify(params.sso_config));
  }
  
  if (params.status) {
    formData.append('status', params.status);
  }
  
  if (params.subscription_tier) {
    formData.append('subscription_tier', params.subscription_tier);
  }
  
  if (params.feature_codes) {
    formData.append('feature_codes', JSON.stringify(params.feature_codes));
  }
  
  const response = await api.put<Organization>(
    apiRoutes.organizations.update(organizationId),
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  
  return response.data;
};

// ============================================================================
// LOGO MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Upload a logo file
 * POST /api/v2/logos/?organization_id={organization_id}
 */
export const uploadLogo = async (params: UploadLogoParams): Promise<Logo> => {
  const formData = new FormData();
  formData.append('file', params.file);
  
  const response = await api.post<Logo>(
    `${apiRoutes.logos.upload}?organization_id=${params.organization_id}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  
  return response.data;
};

/**
 * Get logo file by ID
 * GET /api/v2/logos/{logo_id}
 * Returns the actual image file as a Blob
 */
export const getLogoFile = async (logoId: string): Promise<Blob> => {
  const response = await api.get(apiRoutes.logos.getFile(logoId), {
    responseType: 'blob',
  });
  
  return response.data;
};

/**
 * Get logo metadata by ID
 * GET /api/v2/logos/{logo_id}/info
 */
export const getLogoMetadata = async (logoId: string): Promise<Logo> => {
  const response = await api.get<Logo>(apiRoutes.logos.getMetadata(logoId));
  
  return response.data;
};

/**
 * List all logos
 * GET /api/v2/logos/
 */
export const listLogos = async (): Promise<Logo[]> => {
  const response = await api.get<Logo[]>(apiRoutes.logos.list);
  
  return response.data;
};

/**
 * Update logo by ID
 * PUT /api/v2/logos/{logo_id}
 */
export const updateLogo = async (logoId: string, file: File): Promise<Logo> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.put<Logo>(
    apiRoutes.logos.update(logoId),
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  
  return response.data;
};

/**
 * Delete logo by ID
 * DELETE /api/v2/logos/{logo_id}
 */
export const deleteLogo = async (logoId: string): Promise<void> => {
  await api.delete(apiRoutes.logos.delete(logoId));
};

/**
 * Get logo by organization ID
 * GET /api/v2/logos/organization/{organization_id}
 */
export const getLogoByOrganization = async (organizationId: string): Promise<Logo> => {
  const response = await api.get<Logo>(apiRoutes.logos.getByOrganization(organizationId));
  
  return response.data;
};

/**
 * Get logo by tenant ID
 * GET /api/v2/logos/tenant/{tenant_id}
 */
export const getLogoByTenant = async (tenantId: string): Promise<Logo> => {
  const response = await api.get<Logo>(apiRoutes.logos.getByTenant(tenantId));
  
  return response.data;
};

